import { useCallback, useEffect, useState } from 'react';
import { Rss } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { collection, getCountFromServer, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { spaceWeatherWaveformRingConfigs } from './spaceWeatherWaveformConfig';
import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';
// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

interface SpaceWeatherSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

export function SpaceWeatherSettingsDialog({ open, onClose, onRefresh }: SpaceWeatherSettingsDialogProps) {
  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [activeFeedIds, setActiveFeedIds] = useState<string[]>([]);

  const loadRecordCount = useCallback(async () => {
    const db = await getDb();
    if (!db) {
      setError('Firestore not configured.');
      setLoading(false);
      return;
    }
    try {
      const snap = await getCountFromServer(collection(db, 'spaceWeatherReports'));
      setRecordCount(snap.data().count);
    } catch (e: any) {
      setError('Failed to load count: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setActiveFeedIds([]);
    void loadRecordCount();
  }, [open, loadRecordCount]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadActiveFeeds = async () => {
      try {
        const db = await getDb();
        if (!db) return;
        const snap = await getDocs(collection(db, 'spaceWeatherReports'));
        if (cancelled) return;
        const ringIds = new Set<string>();
        snap.docs.forEach((docSnap) => {
          const report = docSnap.data();
          spaceWeatherWaveformRingConfigs.forEach((ring) => {
            if (ring.parseValue(report)) {
              ringIds.add(ring.id);
            }
          });
        });
        if (!cancelled) {
          setActiveFeedIds(Array.from(ringIds));
        }
      } catch (e: any) {
        if (!cancelled) {
          setError('Failed to read configured feeds: ' + (e?.message || e));
        }
      }
    };

    void loadActiveFeeds();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Example: delete oldest records to keep under 2000
  const handleTrimRecords = async () => {
    setDeleting(true);
    setStatus('Trimming...');
    try {
      const db = await getDb();
      if (!db) throw new Error('Firestore not configured.');
      const snap = await getDocs(collection(db, 'spaceWeatherReports'));
      const docs = snap.docs.sort((a, b) => (a.data().pubDate || '').localeCompare(b.data().pubDate || ''));
      const toDelete = docs.length > 2000 ? docs.slice(0, docs.length - 2000) : [];
      for (const d of toDelete) {
        await deleteDoc(doc(db, 'spaceWeatherReports', d.id));
      }
      setStatus(`Trimmed ${toDelete.length} records.`);
      await loadRecordCount();
      if (onRefresh) onRefresh();
    } catch (e: any) {
      setError('Failed to trim: ' + (e?.message || e));
    } finally {
      setDeleting(false);
    }
  };

  const handleReloadRss = async () => {
    setReloading(true);
    setStatus('Reloading space weather feed…');
    setError(null);
    try {
      const response = await fetch(buildAppsationsFunctionUrl('proxySpaceWeatherRss'), { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setStatus('Space weather feed reloaded.');
      await loadRecordCount();
      if (onRefresh) onRefresh();
    } catch (e: any) {
      setError('Failed to reload RSS feed: ' + (e?.message || e));
    } finally {
      setReloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Space Weather Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <div className="font-semibold">Record count:</div>
            {loading ? 'Loading…' : recordCount}
          </div>
          <div>
            <div className="font-semibold">Active monitored feeds:</div>
            <div className="mt-2 overflow-hidden rounded-lg border border-border/60">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Feed</th>
                    <th className="px-3 py-2 text-left font-semibold">Waveform</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {spaceWeatherWaveformRingConfigs.map((ring) => {
                    const isActive = activeFeedIds.includes(ring.id);
                    return (
                      <tr key={ring.id} className="border-t border-border/40">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 text-foreground">
                            <span className="text-muted-foreground">{ring.icon}</span>
                            <span className="font-medium">{ring.label}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">{ring.description}</div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{ring.waveform}</td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              isActive
                                ? 'inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400'
                                : 'inline-flex items-center gap-1 rounded-full bg-muted/50 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground'
                            }
                          >
                            <Rss className="h-3 w-3" />
                            {isActive ? 'Active' : 'No recent data'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <Button variant="secondary" onClick={handleReloadRss} disabled={reloading}>
              Reload RSS data
            </Button>
          </div>
          <div>
            <Button variant="destructive" onClick={handleTrimRecords} disabled={deleting}>
              Trim to 2000 records
            </Button>
            {status && <div className="text-xs text-blue-400 mt-1">{status}</div>}
          </div>
          {error && <div className="text-xs text-red-400">{error}</div>}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
